/**
 * 11 §8 presentation beats as pure styles. The renderer feeds the match phase,
 * ms into the phase, the LIVE timer and the results-visible age; this returns
 * the world-side treatment for the frame. No clock, no DOM, no state.
 */
import type { MatchPhase, Team } from '../../../sim/types.js'

export interface BeatInput {
  mode: 'hub' | 'match'
  phase: MatchPhase
  /** ms into the current phase (phaseTicks × tick length). */
  phaseMs: number
  /** LIVE timer in seconds, or null outside LIVE. */
  timerS: number | null
  /** Winner of the round that just ended (ROUND_END), or null. */
  winner: Team | null
  /** ms since the results panel appeared, or null while it is hidden. */
  resultsSinceMs: number | null
  reducedMotion: boolean
}

export interface BeatStyle {
  /** 0 during COUNTDOWN, pours 0→1 over GO_POUR_MS after GO (LIVE, phaseMs < 80). */
  heldDiscFill: number
  /** 0..1 at 1 Hz while timerS ≤ 10 in LIVE, else 0. */
  seamPulse: number
  /** OVERTIME first frame (phaseMs < 17) unless reducedMotion. */
  strokeOnlyFrame: boolean
  /** ROUND_END phaseMs < 34 → the winning team. */
  winnerBright: Team | null
  /** ROUND_END: discs start un-drawing this many ms into the phase (consumers compare phaseMs). */
  discsUndrawAtMs: number
  /** 0..1 over WORLD_DIM_MS after resultsSinceMs (stroke-only behind the results panel). */
  worldDim: number
  /** BETWEEN rounds: discs outline-only. */
  betweenOutline: boolean
}

export const GO_POUR_MS = 80
export const LAST10_S = 10
export const OT_STROKE_FRAME_MS = 17
export const WINNER_BRIGHT_MS = 34
export const ROUND_END_DISC_UNDRAW_MS = 1800
export const WORLD_DIM_MS = 600

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v))

export function beatStyle(i: BeatInput): BeatStyle {
  const live = i.phase === 'LIVE'
  const heldDiscFill = i.mode === 'hub' ? 1 : i.phase === 'COUNTDOWN' ? 0 : live ? clamp01(i.phaseMs / GO_POUR_MS) : 1
  const seamPulse = live && i.timerS !== null && i.timerS <= LAST10_S ? 0.5 + 0.5 * Math.cos(i.timerS * Math.PI * 2) : 0
  const strokeOnlyFrame = i.phase === 'OVERTIME' && i.phaseMs < OT_STROKE_FRAME_MS && !i.reducedMotion
  const winnerBright = i.phase === 'ROUND_END' && i.phaseMs < WINNER_BRIGHT_MS ? i.winner : null
  const worldDim = i.resultsSinceMs === null ? 0 : clamp01(i.resultsSinceMs / WORLD_DIM_MS)
  return { heldDiscFill, seamPulse, strokeOnlyFrame, winnerBright, discsUndrawAtMs: ROUND_END_DISC_UNDRAW_MS, worldDim, betweenOutline: i.phase === 'BETWEEN' }
}
